import { DIGISAKA_API_ORIGIN } from '../../../config/digisakaApiOrigin';
import { CAVITE_SILANG_COG_URLS } from './caviteSilangLayers';
import { ISABELA_PHILRICE_COG_URLS } from './isabelaPhilriceLayers';
import type { LayerVisibilityState } from '../utils/MapLayerManager';

export type DroneMissionKey = 'isabela' | 'cavite';

export interface DroneMissionConfig {
  key: DroneMissionKey;
  label: string;
  ndviCogUrl: string;
  orthoCogUrl: string;
  bbox: [number, number, number, number];
  ndviLayerKey: 'isabelaPhilriceNDVI' | 'caviteSilangNDVI';
  /** Ground sample distance — shown in prescription map metadata. */
  spatialResolutionCm?: number;
  /** Human-readable survey period (e.g. "October 2024"). */
  capturePeriod?: string;
}

export const DRONE_NDVI_RANGE: [number, number] = [-0.4, 0.6];

/**
 * Linear NDVI → rice yield model (illustrative; calibrate with local harvest data).
 * Yield (t/ha) = intercept + slope × NDVI — common form in rice remote-sensing studies.
 */
export const NDVI_YIELD_MODEL = {
  intercept: 4,
  slope: 6,
} as const;

/** Map NDVI to estimated grain yield (t/ha). */
export function ndviToEstimatedYieldTonsHa(ndvi: number): number {
  const { intercept, slope } = NDVI_YIELD_MODEL;
  const yieldTons = intercept + slope * ndvi;
  return Math.max(0, yieldTons);
}

export interface VegetationClassDef {
  id: string;
  label: string;
  min: number;
  max: number;
  color: string;
}

/** Four-class vegetation zones for drone NDVI analytics. */
export const DRONE_VEGETATION_CLASSES: VegetationClassDef[] = [
  { id: 'very_low', label: 'Very Low', min: -0.4, max: -0.2, color: '#d73027' },
  { id: 'low', label: 'Low', min: -0.2, max: 0.0, color: '#fdae61' },
  { id: 'medium', label: 'Medium', min: 0.0, max: 0.3, color: '#fee08b' },
  { id: 'high', label: 'High', min: 0.3, max: 0.6, color: '#1a9850' },
];

/** @deprecated Use DRONE_VEGETATION_CLASSES */
export const DRONE_PRESCRIPTION_CLASSES = DRONE_VEGETATION_CLASSES;

/** Midpoint NDVI for a class — used in legend yield hints. */
export function vegetationClassMidpointNdvi(cls: VegetationClassDef): number {
  return (cls.min + cls.max) / 2;
}

export const DRONE_MISSIONS: DroneMissionConfig[] = [
  {
    key: 'isabela',
    label: 'Isabela PhilRice',
    ndviCogUrl: ISABELA_PHILRICE_COG_URLS.ndvi,
    orthoCogUrl: ISABELA_PHILRICE_COG_URLS.ortho,
    bbox: [121.59505679467499, 16.876529833676, 121.59891385851898, 16.879314985676],
    ndviLayerKey: 'isabelaPhilriceNDVI',
    spatialResolutionCm: 2,
  },
  {
    key: 'cavite',
    label: 'Cavite Silang',
    ndviCogUrl: CAVITE_SILANG_COG_URLS.ndvi,
    orthoCogUrl: CAVITE_SILANG_COG_URLS.ortho,
    bbox: [120.98690150017501, 14.242578462121, 120.99130550646001, 14.245994406611999],
    ndviLayerKey: 'caviteSilangNDVI',
    spatialResolutionCm: 2,
    capturePeriod: 'October 2024',
  },
];

const TITILER_TILE_BASE = DIGISAKA_API_ORIGIN + '/api/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png';

/** Prescription colormap tiles — single-band NDVI with red (low) → green (high). */
export function buildDroneNdviPrescriptionTileUrl(cogUrl: string): string {
  const [min, max] = DRONE_NDVI_RANGE;
  const params = new URLSearchParams();
  params.set('url', cogUrl);
  params.set('bidx', '1');
  params.set('rescale', `${min},${max}`);
  params.set('colormap_name', 'rdylgn');
  params.set('nodata', 'nan');
  params.set('format', 'png');
  params.set('resampling', 'lanczos');
  return `${TITILER_TILE_BASE}?${params.toString()}`;
}

export function getActiveDroneNdviMission(
  layerVisibility: LayerVisibilityState
): DroneMissionConfig | null {
  for (const mission of DRONE_MISSIONS) {
    if (layerVisibility[mission.ndviLayerKey]) return mission;
  }
  return null;
}

/** Mission footprint as GeoJSON polygon (used when no field is drawn). */
export function bboxToGeoJsonPolygon(
  bbox: [number, number, number, number]
): GeoJSON.Polygon {
  const [minX, minY, maxX, maxY] = bbox;
  return {
    type: 'Polygon',
    coordinates: [
      [
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
        [minX, minY],
      ],
    ],
  };
}

export function resolveAnalysisGeometry(
  fieldGeoJson: Record<string, unknown> | null | undefined,
  mission: DroneMissionConfig
): GeoJSON.Geometry | null {
  if (fieldGeoJson) {
    if (fieldGeoJson.type === 'Feature') {
      return (fieldGeoJson as GeoJSON.Feature).geometry ?? null;
    }
    if (
      fieldGeoJson.type === 'Polygon' ||
      fieldGeoJson.type === 'MultiPolygon' ||
      fieldGeoJson.type === 'GeometryCollection'
    ) {
      return fieldGeoJson as GeoJSON.Geometry;
    }
    if (fieldGeoJson.type === 'FeatureCollection') {
      const feat = (fieldGeoJson as GeoJSON.FeatureCollection).features?.[0];
      return feat?.geometry ?? null;
    }
    if (fieldGeoJson.geometry && typeof fieldGeoJson.geometry === 'object') {
      return fieldGeoJson.geometry as GeoJSON.Geometry;
    }
  }
  return bboxToGeoJsonPolygon(mission.bbox);
}
